import { useCallback, useEffect } from 'react'

import { EuiSelectOption } from '@equipmentshare/ds2'
import ReasonPicker, { ReasonPickerProps } from 'components/ReasonPicker'
import { IncrementOrDecrement, ManualAdjustmentReason, ManualAdjustmentReasonType } from '../../../../../../graphql'
import useCanCreateEcommerceAdjustment from 'hooks/useCanCreateEcommerceAdjustment'
import useEcommAdjustmentFlag from 'hooks/useEcommAdjustmentFlag'
import useCanCreateRefurbishedAdjustment from 'hooks/useCanCreateRefurbishedAdjustment'
import useRefurbishedAdjustmentFlag from 'hooks/useRefurbishedAdjustmentFlag'

export type ReasonSelectProps = ReasonPickerProps & {
	storeId?: number,
	branchId?: string | null,
}

export default function ReasonSelect( { selectedReason, onChange, reasonType, storeId, branchId, ...props }: ReasonSelectProps ) {
	const ecommAdjustmentEnabled = useEcommAdjustmentFlag( branchId )
	const canCreateEcommerceAdjustment = useCanCreateEcommerceAdjustment( storeId )
	const canUseEcommReasons = ecommAdjustmentEnabled && canCreateEcommerceAdjustment
	const refurbishedAdjustmentEnabled = useRefurbishedAdjustmentFlag( branchId )
	const canCreateRefurbishedAdjustment = useCanCreateRefurbishedAdjustment( storeId )
	const canUseRefurbishedReason = refurbishedAdjustmentEnabled && canCreateRefurbishedAdjustment
	const filterOptions = useCallback( ( option: EuiSelectOption ) => {
		if ( reasonType === IncrementOrDecrement.Increment ) {
			if ( option.value === ManualAdjustmentReasonType.FoundItems ) {
				return true
			}
			if ( option.value === ManualAdjustmentReasonType.EcommercePartsReturn ) {
				return canUseEcommReasons
			}
			if ( option.value === ManualAdjustmentReasonType.RefurbishedPart ) {
				return canUseRefurbishedReason
			}
			return false
		}

		if ( option.value === ManualAdjustmentReasonType.EcommercePartsSale && !canUseEcommReasons ) {
			return false
		}

		return true
	}, [ reasonType, canUseEcommReasons, canUseRefurbishedReason ] )

	useEffect( () => {
		if ( reasonType === IncrementOrDecrement.Increment ) {
			// Create a partial reason object for the "Found Items" default selection
			const foundItemsReason: Partial<ManualAdjustmentReason> = {
				reason: ManualAdjustmentReasonType.FoundItems,
				description: 'Found items',
				allowIncrement: true,
				allowDecrement: false,
			}
			// Cast is needed because we're creating a minimal reason object without all required GraphQL fields (id, __typename, etc.)
			onChange( ManualAdjustmentReasonType.FoundItems, foundItemsReason as ManualAdjustmentReason )
		} else {
			onChange( undefined, undefined )
		}
	}, [ reasonType ] )
	return (
		<ReasonPicker
			// `isInvalid` from the call site, and `label`/`error`/`helperText` from a
			// wrapping `FormRow` under MUI, only reach the control through here.
			{...props}
			selectedReason={selectedReason}
			onChange={onChange}
			reasonType={reasonType}
			disabled={reasonType === undefined || ( reasonType === IncrementOrDecrement.Increment && !canUseEcommReasons && !canUseRefurbishedReason )}
			filterOptions={filterOptions}
		/>
	)
}
